import { useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, ShieldOff, ShieldCheck } from "lucide-react";
import api, { formatDetail } from "@/lib/api";

export const ResetMfaButton = ({ userType, user, disabled, onDone }) => {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const doReset = async () => {
        setBusy(true); setErr("");
        try {
            await api.post(`/admin/users/${userType}/${user.user_id}/reset-mfa`);
            setOpen(false);
            onDone?.();
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    return (
        <>
            <span data-testid={`mfa-status-${user.user_id}`}
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${user.mfa_enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                <ShieldCheck className="w-3 h-3" /> {user.mfa_enabled ? "2FA on" : "2FA pending"}
            </span>
            <button type="button" disabled={disabled || !user.mfa_enabled} onClick={() => { setErr(""); setOpen(true); }}
                    data-testid={`reset-mfa-btn-${user.user_id}`}
                    className="text-violet-600 hover:text-violet-800 underline inline-flex items-center gap-1 text-xs disabled:opacity-30 disabled:no-underline">
                <ShieldOff className="w-3 h-3" /> Reset 2FA
            </button>
            {open && createPortal(
                <div data-testid="reset-mfa-modal" className="fixed inset-0 bg-black/40 flex items-center justify-center z-[200] p-4">
                    <div className="bg-white rounded-xl p-6 w-[440px] shadow-xl text-left">
                        <h3 className="font-bold text-base mb-1">Reset two-factor authentication?</h3>
                        <p className="text-xs text-slate-600 mb-2">
                            <span className="font-mono font-semibold">{user.email}</span> will be signed out of all devices and must scan a new QR code at their next login. Their old authenticator and backup codes stop working.
                        </p>
                        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-4">
                            Only do this after verifying the user's identity out-of-band (phone/video call). This action is recorded in the audit log.
                        </p>
                        {err && <div data-testid="reset-mfa-error" className="text-xs text-rose-600 mb-3">{err}</div>}
                        <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => setOpen(false)} data-testid="reset-mfa-cancel" className="btn-secondary px-3 py-2 rounded-md text-xs">Cancel</button>
                            <button type="button" onClick={doReset} disabled={busy} data-testid="reset-mfa-confirm"
                                    className="px-3 py-2 rounded-md text-xs bg-violet-600 text-white hover:bg-violet-700 font-semibold flex items-center gap-1.5">
                                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Reset 2FA
                            </button>
                        </div>
                    </div>
                </div>, document.body)}
        </>
    );
};
